import { Fragment, useEffect, useMemo, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { ApiError } from '../../api/client';
import {
  getAktivitaBehavioralSummary,
  getAktivitaBehavioralHeatmap,
  getAktivitaBehavioralWeather,
  type BehavioralSummary,
  type BehavioralHeatmap,
  type BehavioralWeather,
} from '../../api/rondo';
import { useTabParam } from '../../shell/useTabParam';
import { ArcSelect, ArcTabs, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Card } from '../../components/arc/card/card';
import { PageState } from '../../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../../components/arc/sortable-data-table/sortable-data-table';
import { fmtNum } from './aktivita-shared';
import './behavioral.css';
import './aktivita-behavioral-arc.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Tooltip, Legend);

function fmtDate(s: string | null): string {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '-';
  return (
    d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
  );
}
function fmtAge(s: string | null): string {
  if (!s) return '-';
  const sec = Math.round((Date.now() - new Date(s).getTime()) / 1000);
  if (sec < 60) return sec + ' s zpět';
  if (sec < 3600) return Math.round(sec / 60) + ' min zpět';
  if (sec < 86400) return Math.round(sec / 3600) + ' h zpět';
  return Math.round(sec / 86400) + ' d zpět';
}

const PALETTE = ['#0058b3', '#5b3f96', '#16a34a', '#d97706', '#dc2626', '#0891b2', '#a855f7', '#6b7280'];

type TabKey = 'zakladni' | 'heatmap' | 'pocasi';
const BEHAVIORAL_TABS: readonly TabKey[] = ['zakladni', 'heatmap', 'pocasi'];

export function AktivitaBehavioralPage() {
  const [tab, setTab] = useTabParam<TabKey>('tab', BEHAVIORAL_TABS, 'zakladni');
  const [data, setData] = useState<BehavioralSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAktivitaBehavioralSummary()
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <PageState title="Načítám Behavioral" loading />;
  if (error) return <PageState title="Behavioral se nepodařilo načíst" message={error} />;
  if (!data) return null;

  return (
    <section className="activity-behavioral-page rondo-arc-behavioral p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>Behavioral</h1><p>Události, rytmus aktivity a vliv počasí na chování uživatelů</p></div></header>
      <ArcTabs label="Behavioral" value={tab} onChange={setTab} items={[{ value: 'zakladni', label: 'Základní' }, { value: 'heatmap', label: 'Čas aktivity' }, { value: 'pocasi', label: 'Počasí' }]} />

      <div>
        {tab === 'zakladni' && <TabZakladni data={data} />}
        {tab === 'heatmap' && <TabHeatmap data={data} />}
        {tab === 'pocasi' && <TabPocasi />}
      </div>
    </section>
  );
}

function TabZakladni({ data }: { data: BehavioralSummary }) {
  const { totals, topTypes, perDay, mirror } = data;
  type EventRow = BehavioralSummary['topTypes'][number] & Record<string, unknown>;
  const eventColumns: DataColumn<EventRow>[] = [
    { key: 'event_type', label: 'Event', width: 430 },
    { key: 'cnt', label: 'Počet', width: 170, numeric: true, render: (_, row) => fmtNum(row.cnt) },
    { key: 'unique_users', label: 'Unikátní', width: 170, numeric: true, render: (_, row) => fmtNum(row.unique_users) },
    { key: 'last_at', label: 'Poslední', width: 200, render: (_, row) => fmtDate(row.last_at) },
  ];

  return (
    <div role="tabpanel">
      <div className="rondo-arc-behavioral-kpis">
        <Card title="Eventů celkem" className="rondo-arc-behavioral-kpi"><strong>{fmtNum(totals.total)}</strong><p>Od začátku ingestu</p></Card>
        <Card title="Unikátní uživatelé" className="rondo-arc-behavioral-kpi"><strong>{fmtNum(totals.unique_users)}</strong><p>S alespoň 1 eventem</p></Card>
        <Card title="Poslední event" className="rondo-arc-behavioral-kpi"><strong>{fmtAge(totals.last_event)}</strong><p>Od {fmtDate(totals.first_event)}</p></Card>
      </div>

      <div className="rondo-arc-behavioral-section-heading"><h2>Top eventů (posledních 30 dní)</h2><span>{topTypes.length} typů</span></div>
      <div className="rondo-arc-behavioral-table"><SortableDataTable rows={topTypes as EventRow[]} columns={eventColumns} rowKey="event_type" caption="Top eventů" emptyMessage="Zatím žádné eventy. Daemon ingestuje každých 5 min." /></div>

      <Card title="Eventy za den" description="Posledních 30 dní" className="rondo-arc-behavioral-chart"><PerDayChart perDay={perDay} /></Card>

      <div className="rondo-arc-behavioral-section-heading"><h2>Stav ingestu</h2></div>
      <SourcesTable mirror={mirror} />
    </div>
  );
}

function PerDayChart({ perDay }: { perDay: BehavioralSummary['perDay'] }) {
  const { labels, datasets } = useMemo(() => {
    const byDay: Record<string, Record<string, number>> = {};
    const types = new Set<string>();
    for (const r of perDay) {
      const bucket = (byDay[r.day] ??= {});
      bucket[r.event_type] = Number(r.cnt);
      types.add(r.event_type);
    }
    const days = Object.keys(byDay).sort();
    const ds = [...types].map((t, i) => ({
      label: t,
      data: days.map((d) => byDay[d]?.[t] || 0),
      backgroundColor: PALETTE[i % PALETTE.length],
      stack: 's',
    }));
    return {
      labels: days.map((d) => new Date(d).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' })),
      datasets: ds,
    };
  }, [perDay]);

  const options: any = {
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } },
    scales: {
      x: { stacked: true, ticks: { font: { size: 10 } } },
      y: { stacked: true, beginAtZero: true, ticks: { precision: 0, font: { size: 11 } } },
    },
  };

  return (
    <div style={{ position: 'relative', height: 280 }}>
      <Bar data={{ labels, datasets }} options={options} />
    </div>
  );
}

function SourcesTable({ mirror }: { mirror: BehavioralSummary['mirror'] }) {
  const rows = [
    {
      source_name: 'behavioral',
      ingested_total: mirror.rows,
      last_id: '-',
      last_at: mirror.data_through_date,
      last_tick_at: mirror.last_success_at,
    },
  ];
  type SourceRow = (typeof rows)[number] & Record<string, unknown>;
  const columns: DataColumn<SourceRow>[] = [
    { key: 'source_name', label: 'Zdroj', width: 240 },
    { key: 'ingested_total', label: 'Ingested', width: 185, numeric: true, render: (_, row) => fmtNum(row.ingested_total) },
    { key: 'last_id', label: 'Last ID', width: 170 },
    { key: 'last_at', label: 'Last event', width: 240, render: (_, row) => fmtDate(row.last_at) },
    { key: 'last_tick_at', label: 'Last tick', width: 170, render: (_, row) => fmtAge(row.last_tick_at) },
  ];
  return (
    <div className="rondo-arc-behavioral-table"><SortableDataTable rows={rows as SourceRow[]} columns={columns} rowKey="source_name" caption="Stav ingestu" /></div>
  );
}

const DAY_LABELS: Record<number, string> = { 2: 'Po', 3: 'Út', 4: 'St', 5: 'Čt', 6: 'Pá', 7: 'So', 1: 'Ne' };
const DAY_ORDER = [2, 3, 4, 5, 6, 7, 1];

function TabHeatmap({ data }: { data: BehavioralSummary }) {
  const [eventType, setEventType] = useState('');
  const [heatmap, setHeatmap] = useState<BehavioralHeatmap | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAktivitaBehavioralHeatmap(eventType || undefined)
      .then((j) => !cancelled && setHeatmap(j))
      .catch(() => !cancelled && setHeatmap({ cells: [] }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventType]);

  return (
    <div role="tabpanel">
      <Card title="Aktivita podle dne a hodiny" description="Posledních 30 dní" className="rondo-arc-behavioral-heatmap">
          <ArcSelect label="Event" value={eventType} onChange={setEventType} options={[{ value: '', label: 'Všechny' }, ...data.topTypes.map((item) => ({ value: item.event_type, label: `${item.event_type} (${item.cnt})` }))]} />
          {loading ? (
            <PageState title="Načítám aktivitu" loading />
          ) : (
            <div className="rondo-arc-behavioral-heatmap-scroll"><HeatmapGrid cells={heatmap?.cells ?? []} /></div>
          )}
          <p className="rondo-arc-behavioral-note">Tmavší políčko znamená více eventů. Pruhy ukazují, kdy je vybraný segment nejčastěji aktivní.</p>
      </Card>
    </div>
  );
}

function HeatmapGrid({ cells }: { cells: BehavioralHeatmap['cells'] }) {
  if (!cells.length) {
    return <div className="text-muted small">Žádná data pro vybraný event.</div>;
  }
  const matrix: Record<number, Record<number, number>> = {};
  let maxVal = 0;
  for (const c of cells) {
    const row = (matrix[c.dow] ??= {});
    row[c.hour] = Number(c.cnt);
    if (Number(c.cnt) > maxVal) maxVal = Number(c.cnt);
  }
  function color(v: number): string | null {
    if (!v) return null;
    const intensity = Math.min(1, v / maxVal);
    const r = Math.round(0 + (0 - 0) * intensity);
    const g = Math.round(88 + (60 - 88) * intensity);
    const b = Math.round(179 + (54 - 179) * intensity);
    return `rgb(${r},${g},${b})`;
  }
  return (
    <div className="mb-heatmap-grid">
      <div className="mb-heatmap-corner" />
      {Array.from({ length: 24 }, (_, h) => (
        <div key={'h' + h} className="mb-heatmap-hour">
          {h}
        </div>
      ))}
      {DAY_ORDER.map((d) => (
        <Fragment key={'d' + d}>
          <div className="mb-heatmap-day">{DAY_LABELS[d]}</div>
          {Array.from({ length: 24 }, (_, h) => {
            const v = matrix[d]?.[h] || 0;
            const c = color(v);
            return (
              <div
                key={'c' + d + '-' + h}
                className={'mb-heatmap-cell' + (v ? '' : ' l0')}
                style={c ? { background: c } : undefined}
                title={`${DAY_LABELS[d]} ${h}:00 - ${v} eventů`}
              >
                {v > 0 ? v : ''}
              </div>
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}

// ============================================================================
// TAB 3 - Počasí
// ============================================================================
function corrTag(v: number | null): string | null {
  if (v == null) return null;
  const a = Math.abs(v);
  const sila = a >= 0.5 ? 'silná' : a >= 0.3 ? 'střední' : a >= 0.15 ? 'slabá' : 'zanedbatelná';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)} (${sila})`;
}

function TabPocasi() {
  const [country, setCountry] = useState('CZ');
  const [days, setDays] = useState(180);
  const [wx, setWx] = useState<BehavioralWeather | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAktivitaBehavioralWeather(country, days)
      .then((j) => !cancelled && setWx(j))
      .catch(() => !cancelled && setWx(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [country, days]);

  const meta = wx
    ? `${wx.country} · ${wx.rows.length} dní · ${wx.days} d okno`
    : 'Klikni na záložku Počasí pro načtení…';

  const corrSh = wx ? corrTag(wx.meta.correlation_stay_home_vs_events) : null;
  const corrVac = wx ? corrTag(wx.meta.correlation_vacation_vs_events) : null;
  const showCorr = corrSh != null || corrVac != null;

  return (
    <div role="tabpanel">
      <ArcToolbar className="rondo-arc-behavioral-toolbar" end={<small>{meta}</small>}>
        <ArcSelect label="Trh" value={country} onChange={setCountry} options={[{ value: 'CZ', label: 'CZ' }, { value: 'SK', label: 'SK' }, { value: 'RO', label: 'RO' }]} />
        <ArcSelect label="Období" value={String(days)} onChange={(value) => setDays(Number(value))} options={[{ value: '90', label: '90 dní' }, { value: '180', label: '180 dní' }, { value: '365', label: '365 dní' }, { value: '730', label: '2 roky' }]} />
      </ArcToolbar>

      {showCorr && (
        <div className="rondo-arc-behavioral-notice">
          Pearson korelace: stay_home_idx vs. events = {corrSh ?? '-'} · vacation_intensity vs. events = {corrVac ?? '-'}
        </div>
      )}

      <Card title="Zapojení, počasí a sezóna" className="rondo-arc-behavioral-chart">
          {loading ? (
            <PageState title="Načítám počasí" loading />
          ) : !wx || wx.available === false ? (
            <p>Data o počasí zatím nejsou k dispozici.</p>
          ) : (
            <WeatherChart rows={wx.rows} />
          )}
      </Card>

      <div className="rondo-arc-behavioral-note">
        stay_home_idx 0-100 = derivovaný index (déšť+zima+zataženo+vítr). vacation_intensity 0-100 = sezóna dovolených. events_total = denní suma user events.
      </div>
    </div>
  );
}

function WeatherChart({ rows }: { rows: BehavioralWeather['rows'] }) {
  const labels = rows.map((r) => new Date(r.day).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' }));
  const data = {
    labels,
    datasets: [
      {
        label: 'Events total',
        data: rows.map((r) => r.events_total),
        borderColor: '#1d4ed8',
        backgroundColor: 'rgba(29,78,216,.1)',
        yAxisID: 'y',
        borderWidth: 2,
        pointRadius: 0,
        fill: true,
      },
      {
        label: 'Stay-home index',
        data: rows.map((r) => r.stay_home_idx),
        borderColor: '#0891b2',
        yAxisID: 'y2',
        borderWidth: 1.5,
        borderDash: [4, 4],
        pointRadius: 0,
      },
      {
        label: 'Vacation intensity',
        data: rows.map((r) => r.vacation_intensity),
        borderColor: '#ea580c',
        yAxisID: 'y2',
        borderWidth: 1.5,
        borderDash: [2, 2],
        pointRadius: 0,
      },
    ],
  };
  // options: any - Chart.js types are strict; values 1:1 legacy.
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } },
    scales: {
      x: { ticks: { font: { size: 11 }, maxTicksLimit: 12 } },
      y: { position: 'left', title: { display: true, text: 'Events' }, ticks: { font: { size: 11 } } },
      y2: {
        position: 'right',
        min: 0,
        max: 100,
        title: { display: true, text: 'Index 0-100' },
        grid: { drawOnChartArea: false },
        ticks: { font: { size: 11 } },
      },
    },
  };
  return (
    <div style={{ position: 'relative', height: 340 }}>
      <Line data={data} options={options} />
    </div>
  );
}
